import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react'
import MaterialSelect from '../../../../../components/MaterialSelect'
import { Box } from '@mui/system'
import MaterialTypography from '../../../../../components/MaterialTypography'
import MaterialInput from '../../../../../components/MaterialInput'
import { Grid } from '@mui/material'
import MaterialDatePicker from '../../../../../components/MaterialDatePicker'
import FileSelector from '../../../../../components/fileSelector';
import { fetchData, postData } from '../../../../Utils/Baseurl'
import { useSnackbar } from '../../../../../components/SnackbarProvider'
import { useLoading } from '../../../../../context/loadingcontext'
import { putData } from '../../../../Utils/Baseurl'
import { getProjectsMiddleware } from '../../../../../api/projectMiddleware'

const AddTestPlan = forwardRef(({ onSuccess, editData }, ref) => {
    const [testPlan, setTestPlan] = useState({
        projectName: '',
        testPlanTitle: '',
        startDate: '',
        endDate: '',
        description: '',
        attachments: null,
        oneDriveLink: ''
    })

    const [projectList, setProjectList] = useState([])
    const { showSnackbar } = useSnackbar()
    const { setCirleLoading } = useLoading()

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchTestPlanDetails = useCallback(async (id) => {
        setCirleLoading(true);
        try {
            const response = await fetchData(`api/testplan/${id}/`);
            if (response && response.data) {
                const data = response.data[0] || response.data;
                setTestPlan({
                    projectName: projectList.find(p => p.value === data.project_id) || '',
                    testPlanTitle: data.title || '',
                    startDate: data.start_date || '',
                    endDate: data.end_date || '',
                    description: data.description || '',
                    attachments: null,
                    oneDriveLink: ''
                });
            }
        } catch (error) {
            console.error("Error fetching test plan details:", error);
            showSnackbar('Failed to fetch test plan details', 'error');
        } finally {
            setCirleLoading(false);
        }
    }, [projectList, setCirleLoading, showSnackbar]);

    useEffect(() => {
        if (editData?.id) {
            fetchTestPlanDetails(editData.id);
        }
    }, [editData, fetchTestPlanDetails]);

    const fetchProjects = async () => {
        try {
            const userId = sessionStorage.getItem("currentUser");
            if (userId) {
                const response = await getProjectsMiddleware(userId);
                if (response && response.data) {
                    const formattedProjects = response.data.map(proj => ({
                        value: proj.project_id,
                        label: proj.project_name
                    }));
                    setProjectList(formattedProjects);
                }
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        }
    };


    const handleTestPlanChange = (field, value) => {
        setTestPlan((prev) => ({
            ...prev,
            [field]: value
        }))
    }

    const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleSubmit = async () => {
        if (!testPlan.projectName || !testPlan.testPlanTitle || !testPlan.startDate || !testPlan.endDate) {
            showSnackbar('Please fill all required fields', 'error');
            return;
        }

        const userId = sessionStorage.getItem("currentUser");

        const formData = new FormData();
        formData.append('project_id', testPlan.projectName?.value || testPlan.projectName);
        formData.append('title', testPlan.testPlanTitle);
        formData.append('start_date', formatDate(testPlan.startDate));
        formData.append('end_date', formatDate(testPlan.endDate));
        formData.append('owner_id', userId);
        formData.append('description', testPlan.description);
        if (testPlan.attachments) {
            formData.append('attachment', testPlan.attachments);
        }

        setCirleLoading(true);
        try {
            let response;
            if (editData) {
                response = await putData(`api/testplan/${editData.id}/`, formData);
            } else {
                response = await postData('api/testplan/', formData);
            }

            if (response) {
                showSnackbar(editData ? 'Test Plan updated successfully' : 'Test Plan created successfully', 'success');
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error("Error saving test plan:", error);
            showSnackbar(error.response?.data?.message || `Failed to ${editData ? 'update' : 'create'} Test Plan`, 'error');
        } finally {
            setCirleLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        handleSubmit
    }));

    return (
        <Box sx={{
            maxHeight: '400px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
        }}>
            <Grid container direction='row' spacing={1}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <MaterialTypography variant='caption' color='secondary'>Project Name</MaterialTypography>
                        <MaterialSelect
                            label='Project Name'
                            size='small'
                            value={testPlan.projectName}
                            options={projectList}
                            onChange={(val) => handleTestPlanChange('projectName', val)}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <MaterialTypography variant='caption' color='secondary'>Test Plan Title</MaterialTypography>
                        <MaterialInput
                            label='Test Plan Title'
                            size='small'
                            value={testPlan.testPlanTitle}
                            onChange={(e) => handleTestPlanChange('testPlanTitle', e.target.value)}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <MaterialTypography variant='caption' color='secondary'>Start Date</MaterialTypography>
                        <MaterialDatePicker
                            label='Start Date'
                            size='small'
                            value={testPlan.startDate}
                            onChange={(val) => handleTestPlanChange('startDate', val)}
                            input={{ size: 'small' }}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <MaterialTypography variant='caption' color='secondary'>End Date</MaterialTypography>
                        <MaterialDatePicker
                            label='End Date'
                            size='small'
                            value={testPlan.endDate}
                            onChange={(val) => handleTestPlanChange('endDate', val)}
                            input={{ size: 'small' }}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <MaterialTypography variant='caption' color='secondary'>Description</MaterialTypography>
                        <MaterialInput
                            label='Description'
                            size='small'
                            value={testPlan.description}
                            multiline
                            rows={4}
                            onChange={(e) => handleTestPlanChange('description', e.target.value)}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <MaterialTypography variant='caption' color='secondary'>Attachments</MaterialTypography>
                        <FileSelector
                            onFileSelect={(file) => handleTestPlanChange('attachments', file)}
                            fileUrl={testPlan.attachments ? URL.createObjectURL(testPlan.attachments) : null}
                            allowedTypes={['image']}
                            width="100%"
                            height="150px"
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
})

export default AddTestPlan
